// src/pages/AcademyPage.jsx
import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { publicAPI } from '../services/api';
import { useIntersect } from '../hooks/useIntersect';
import { InternshipCard } from './InternshipsPage'; // ← canonical card

const MEDIA_URL = process.env.REACT_APP_MEDIA_URL || 'http://localhost:5001';

function resolveMedia(url) {
  if (!url) return null;
  return url.startsWith('http') ? url : `${MEDIA_URL}${url}`;
}

const TYPE_COLORS = {
  bootcamp:     '#e8a43a',
  workshop:     '#4e9cf5',
  certification:'#3ecf8e',
  course:       '#a78bfa',
  mentorship:   '#f472b6',
};

// ── Program Card ──────────────────────────────────────────
function ProgramCard({ program, idx }) {
  const navigate = useNavigate();
  const [ref, visible] = useIntersect();
  const [hover, setHover] = useState(false);
  const color = TYPE_COLORS[program.type] || 'var(--gold)';
  const src   = resolveMedia(program.media_url || program.thumbnail_url);

  return (
    <div ref={ref}
      onMouseEnter={()=>setHover(true)} onMouseLeave={()=>setHover(false)}
      style={{
        display:'flex', flexDirection:'column',
        background:'var(--bg-2)', borderRadius:16, overflow:'hidden',
        border:`1px solid ${hover?`${color}44`:'var(--border)'}`,
        opacity:visible?1:0, transform:visible?(hover?'translateY(-3px)':'none'):'translateY(24px)',
        transition:`opacity 0.5s ${idx*0.06}s, transform 0.5s ${idx*0.06}s, border-color 0.2s, box-shadow 0.2s`,
        boxShadow:hover?'0 12px 32px rgba(0,0,0,0.25)':'none',
      }}>

      {/* Cover */}
      <div style={{ height:160, position:'relative', flexShrink:0, background:'var(--bg-3)', overflow:'hidden' }}>
        {src ? (
          <img src={src} alt={program.title}
            style={{ width:'100%', height:'100%', objectFit:'cover', transition:'transform 0.5s', transform:hover?'scale(1.06)':'scale(1)' }}
            onError={e=>e.target.parentNode.style.background='var(--bg-3)'}/>
        ) : (
          <div style={{ width:'100%', height:'100%', background:`linear-gradient(135deg, var(--bg-3), ${color}12)`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'3rem', opacity:0.15 }}>◐</div>
        )}
        <div style={{ position:'absolute', inset:0, background:`linear-gradient(to top, rgba(8,10,13,0.8) 0%, transparent 55%)` }}/>
        <div style={{ position:'absolute', top:12, left:12, display:'flex', gap:6 }}>
          <span style={{ fontSize:'0.62rem', fontWeight:700, padding:'4px 10px', borderRadius:20, background:`${color}dd`, color:'#080a0d', textTransform:'capitalize' }}>
            {program.type}
          </span>
          {program.delivery_mode && (
            <span style={{ fontSize:'0.6rem', fontWeight:600, padding:'4px 10px', borderRadius:20, background:'rgba(8,10,13,0.72)', color:'rgba(255,255,255,0.75)', backdropFilter:'blur(4px)' }}>
              {program.delivery_mode}
            </span>
          )}
        </div>
        {program.is_free && (
          <span style={{ position:'absolute', top:12, right:12, fontSize:'0.62rem', fontWeight:700, padding:'4px 10px', borderRadius:20, background:'rgba(62,207,142,0.9)', color:'#fff' }}>Free</span>
        )}
      </div>

      {/* Body */}
      <div style={{ padding:'18px 20px 20px', display:'flex', flexDirection:'column', gap:10, flex:1 }}>
        {program.domain_name && (
          <div style={{ fontSize:'0.62rem', color:'var(--t-muted)', textTransform:'uppercase', letterSpacing:'0.08em' }}>{program.domain_name}</div>
        )}
        <h3 style={{ fontFamily:'var(--font-display)', fontSize:'1rem', fontWeight:600, lineHeight:1.3, color:hover?color:'var(--t-primary)', transition:'color 0.15s' }}>
          {program.title}
        </h3>
        <p style={{ fontSize:'0.83rem', color:'var(--t-secondary)', lineHeight:1.65, flex:1 }}>
          {program.summary?.slice(0,120)}{program.summary?.length>120?'…':''}
        </p>

        {/* Stats */}
        <div style={{ display:'flex', gap:16, flexWrap:'wrap', paddingTop:10, borderTop:'1px solid var(--border)' }}>
          {program.duration && (
            <div>
              <div style={{ fontSize:'0.58rem', color:'var(--t-muted)', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:2 }}>Duration</div>
              <div style={{ fontSize:'0.82rem', fontWeight:500 }}>{program.duration}</div>
            </div>
          )}
          {program.next_cohort_date && (
            <div>
              <div style={{ fontSize:'0.58rem', color:'var(--t-muted)', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:2 }}>Next Cohort</div>
              <div style={{ fontSize:'0.82rem', fontWeight:600, color }}>{new Date(program.next_cohort_date).toLocaleDateString('en-RW',{month:'short',day:'numeric',year:'numeric'})}</div>
            </div>
          )}
          {program.fees_info && (
            <div>
              <div style={{ fontSize:'0.58rem', color:'var(--t-muted)', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:2 }}>Investment</div>
              <div style={{ fontSize:'0.82rem', fontWeight:600, color }}>{program.fees_info}</div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div style={{ display:'flex', gap:8 }}>
          <Link to={`/academy/programs/${program.slug}`}
            style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', padding:'9px', borderRadius:8, border:'1px solid var(--border)', background:'transparent', color:'var(--t-secondary)', fontSize:'0.82rem', textDecoration:'none', transition:'all 0.15s' }}
            onMouseEnter={e=>{e.currentTarget.style.borderColor='var(--border-2)';e.currentTarget.style.color='var(--t-primary)';}}
            onMouseLeave={e=>{e.currentTarget.style.borderColor='var(--border)';e.currentTarget.style.color='var(--t-secondary)';}}>
            View Details
          </Link>
          <button onClick={()=>navigate(`/academy/programs/${program.slug}/apply`)}
            style={{ flex:1, padding:'9px', borderRadius:8, border:'none', background:color, color:'#080a0d', fontSize:'0.82rem', fontWeight:700, cursor:'pointer', transition:'filter 0.15s' }}
            onMouseEnter={e=>e.currentTarget.style.filter='brightness(1.1)'}
            onMouseLeave={e=>e.currentTarget.style.filter='none'}>
            Apply Now
          </button>
        </div>
      </div>
    </div>
  );
}


// ── Program List Row ──────────────────────────────────────
function ProgramListRow({ program }) {
  const navigate = useNavigate();
  const color = TYPE_COLORS[program.type] || 'var(--gold)';
  return (
    <div style={{ display:'flex', alignItems:'center', gap:20, padding:'16px 20px', background:'var(--bg-2)', borderRadius:12, border:'1px solid var(--border)', transition:'all 0.15s' }}
      onMouseEnter={e=>{e.currentTarget.style.borderColor=color;e.currentTarget.style.background='var(--bg-3)';}}
      onMouseLeave={e=>{e.currentTarget.style.borderColor='var(--border)';e.currentTarget.style.background='var(--bg-2)';}}>
      <div style={{ width:48, height:48, borderRadius:10, background:`${color}18`, border:`1px solid ${color}33`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, fontSize:'1.2rem' }}>
        {{'bootcamp':'⚡','workshop':'◐','certification':'◈','course':'◉','mentorship':'◎'}[program.type]||'◐'}
      </div>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:4 }}>
          <span style={{ fontFamily:'var(--font-display)', fontWeight:600, fontSize:'0.95rem' }}>{program.title}</span>
          <span style={{ fontSize:'0.62rem', fontWeight:700, padding:'2px 8px', borderRadius:20, background:`${color}18`, color, textTransform:'capitalize' }}>{program.type}</span>
          {program.is_free && <span style={{ fontSize:'0.62rem', fontWeight:700, padding:'2px 8px', borderRadius:20, background:'rgba(62,207,142,0.12)', color:'#3ecf8e' }}>Free</span>}
        </div>
        <div style={{ fontSize:'0.82rem', color:'var(--t-secondary)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
          {program.summary?.slice(0,120)}{program.summary?.length>120?'…':''}
        </div>
      </div>
      <div style={{ display:'flex', gap:24, flexShrink:0, fontSize:'0.8rem' }}>
        {program.duration && <div><div style={{ fontSize:'0.58rem', color:'var(--t-muted)', textTransform:'uppercase', letterSpacing:'0.06em' }}>Duration</div><div style={{ fontWeight:500 }}>{program.duration}</div></div>}
        {program.fees_info && <div><div style={{ fontSize:'0.58rem', color:'var(--t-muted)', textTransform:'uppercase', letterSpacing:'0.06em' }}>Fee</div><div style={{ fontWeight:600, color }}>{program.fees_info}</div></div>}
      </div>
      <div style={{ display:'flex', gap:8, flexShrink:0 }}>
        <Link to={`/academy/programs/${program.slug}`} style={{ padding:'8px 14px', borderRadius:8, border:'1px solid var(--border)', color:'var(--t-secondary)', fontSize:'0.8rem', textDecoration:'none', transition:'all 0.15s' }}>Details</Link>
        <button onClick={()=>navigate(`/academy/programs/${program.slug}/apply`)} style={{ padding:'8px 14px', borderRadius:8, border:'none', background:color, color:'#080a0d', fontSize:'0.8rem', fontWeight:700, cursor:'pointer' }}>Apply</button>
      </div>
    </div>
  );
}

// ── Internship List Row ───────────────────────────────────
function InternshipListRow({ internship }) {
  const isOpen = internship.status === 'open';
  return (
    <Link to={`/academy/internships/${internship.slug}`}
      style={{ display:'flex', alignItems:'center', gap:20, padding:'16px 20px', background:'var(--bg-2)', borderRadius:12, border:'1px solid var(--border)', textDecoration:'none', transition:'all 0.15s' }}
      onMouseEnter={e=>{e.currentTarget.style.borderColor='rgba(232,164,58,0.35)';e.currentTarget.style.background='var(--bg-3)';}}
      onMouseLeave={e=>{e.currentTarget.style.borderColor='var(--border)';e.currentTarget.style.background='var(--bg-2)';}}>
      <div style={{ width:48, height:48, borderRadius:10, background:'rgba(232,164,58,0.1)', border:'1px solid rgba(232,164,58,0.25)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, fontSize:'1.2rem' }}>◉</div>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:4 }}>
          <span style={{ fontFamily:'var(--font-display)', fontWeight:600, fontSize:'0.95rem', color:'var(--t-primary)' }}>{internship.title}</span>
          <span style={{ fontSize:'0.62rem', fontWeight:700, padding:'2px 8px', borderRadius:20, background:isOpen?'rgba(62,207,142,0.12)':'var(--bg-3)', color:isOpen?'#3ecf8e':'var(--t-muted)' }}>{isOpen?'Open':internship.status}</span>
          {internship.is_paid && <span style={{ fontSize:'0.62rem', fontWeight:700, padding:'2px 8px', borderRadius:20, background:'rgba(232,164,58,0.12)', color:'var(--gold)' }}>Paid</span>}
          {internship.department && <span style={{ fontSize:'0.62rem', color:'var(--t-muted)', padding:'2px 8px', borderRadius:20, background:'var(--bg-3)' }}>{internship.department}</span>}
        </div>
        <div style={{ fontSize:'0.82rem', color:'var(--t-secondary)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
          {internship.description?.slice(0,120)}{internship.description?.length>120?'…':''}
        </div>
      </div>
      <div style={{ display:'flex', gap:24, flexShrink:0, fontSize:'0.8rem' }}>
        {internship.duration && <div><div style={{ fontSize:'0.58rem', color:'var(--t-muted)', textTransform:'uppercase', letterSpacing:'0.06em' }}>Duration</div><div style={{ fontWeight:500 }}>{internship.duration}</div></div>}
        {internship.is_paid && <div><div style={{ fontSize:'0.58rem', color:'var(--t-muted)', textTransform:'uppercase', letterSpacing:'0.06em' }}>Stipend</div><div style={{ fontWeight:600, color:'var(--gold)' }}>RWF {Number(internship.stipend_amount||0).toLocaleString()}</div></div>}
        {internship.mode && <div><div style={{ fontSize:'0.58rem', color:'var(--t-muted)', textTransform:'uppercase', letterSpacing:'0.06em' }}>Mode</div><div style={{ fontWeight:500 }}>{internship.mode?.replace('_',' ')}</div></div>}
      </div>
      <span style={{ fontSize:'0.82rem', color:'var(--gold)', fontWeight:600, flexShrink:0 }}>Apply →</span>
    </Link>
  );
}

// ── MAIN PAGE ─────────────────────────────────────────────
export default function AcademyPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') || 'programs';

  const [programs,    setPrograms]    = useState([]);
  const [internships, setInternships] = useState([]);
  const [domains,     setDomains]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [activeDomain,setDomain]      = useState('');
  const [activeType,  setType]        = useState(searchParams.get('type') || '');
  const [viewMode,    setViewMode]    = useState('list'); // 'grid' | 'list'

  const setTab = (t) => setSearchParams({ tab:t });

  useEffect(() => {
    publicAPI.programDomains().then(r=>setDomains(r.data?.data||[])).catch(()=>{});
  }, []);

  const loadPrograms = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (activeDomain) params.domain = activeDomain;
      if (activeType)   params.type   = activeType;
      const res = await publicAPI.programs(params);
      setPrograms(res.data?.data||[]);
    } catch { setPrograms([]); } finally { setLoading(false); }
  }, [activeDomain, activeType]);

  const loadInternships = useCallback(async () => {
    setLoading(true);
    try {
      const res = await publicAPI.internships({ status:'open' });
      setInternships(res.data?.data||[]);
    } catch { setInternships([]); } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (tab === 'programs') loadPrograms();
    else loadInternships();
  }, [tab, loadPrograms, loadInternships]);

  const TYPES = ['bootcamp','workshop','certification','course','mentorship'];

  const tabBtn = (t) => ({
    padding:'11px 24px', fontSize:'0.9rem', cursor:'pointer', background:'none', border:'none',
    borderBottom:`2px solid ${tab===t?'var(--gold)':'transparent'}`,
    color:tab===t?'var(--gold)':'var(--t-secondary)', fontWeight:tab===t?700:400,
    fontFamily:tab===t?'var(--font-display)':'inherit', transition:'all 0.15s', marginBottom:-1,
  });

  return (
    <div>
      {/* Hero */}
      <section style={{ paddingTop:'calc(var(--nav-h) + clamp(60px,8vw,100px))', paddingBottom:'clamp(48px,6vw,80px)', background:'linear-gradient(160deg, var(--bg-1) 0%, var(--bg-0) 70%)', position:'relative', overflow:'hidden' }}>
        <div style={{ position:'absolute', top:0, right:0, width:'50%', height:'100%', background:'linear-gradient(135deg, transparent 40%, rgba(232,164,58,0.04) 100%)', pointerEvents:'none' }}/>
        <div className="container" style={{ position:'relative', zIndex:1 }}>
          <div className="section-label label" style={{ marginBottom:20 }}>MilleHills Academy</div>
          <h1 style={{ fontFamily:'var(--font-display)', fontSize:'clamp(2rem,5vw,3.2rem)', fontWeight:700, lineHeight:1.15, marginBottom:16 }}>
            Learn. Build.<br/><span style={{ color:'var(--gold)' }}>Lead. Intern.</span>
          </h1>
          <p style={{ color:'var(--t-secondary)', fontSize:'1rem', maxWidth:540, marginBottom:32, lineHeight:1.8 }}>
            Structured training programs and hands-on internships — built for Rwanda's next generation of innovators.
          </p>
          <div style={{ display:'flex', gap:28, flexWrap:'wrap', marginBottom:32 }}>
            {[{n:programs.length||'10+',l:'Programs'},{n:internships.length||'5+',l:'Open Internships'},{n:domains.length||'8+',l:'Domains'}].map(s=>(
              <div key={s.l}>
                <div style={{ fontFamily:'var(--font-display)', fontSize:'1.6rem', fontWeight:700, color:'var(--gold)', lineHeight:1 }}>{s.n}</div>
                <div style={{ fontSize:'0.78rem', color:'var(--t-muted)', marginTop:3 }}>{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tab bar + filters */}
      <div style={{ background:'var(--bg-0)', borderBottom:'1px solid var(--border)', position:'sticky', top:'var(--nav-h)', zIndex:50 }}>
        <div className="container" style={{ display:'flex', flexDirection:'column' }}>
          <div style={{ display:'flex' }}>
            <button style={tabBtn('programs')} onClick={()=>setTab('programs')}>Training Programs</button>
            <button style={tabBtn('internships')} onClick={()=>setTab('internships')}>
              Internships
              <span style={{ marginLeft:6, fontSize:'0.65rem', padding:'2px 6px', borderRadius:10, background:'rgba(62,207,142,0.15)', color:'#3ecf8e', fontWeight:700 }}>Hiring</span>
            </button>
          </div>
          {tab === 'programs' && (
            <div style={{ padding:'10px 0 12px', display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
              {['', ...TYPES].map(t=>(
                <button key={t} onClick={()=>setType(t)} style={{ padding:'5px 12px', borderRadius:20, fontSize:'0.75rem', cursor:'pointer', textTransform:'capitalize', fontWeight:activeType===t?700:400, background:activeType===t?(TYPE_COLORS[t]||'var(--gold)'):'var(--bg-2)', color:activeType===t?'#080a0d':'var(--t-secondary)', border:`1px solid ${activeType===t?(TYPE_COLORS[t]||'var(--gold)'):'var(--border)'}`, transition:'all 0.15s' }}>
                  {t||'All Types'}
                </button>
              ))}
              {domains.length > 0 && (
                <>
                  <div style={{ width:1, height:18, background:'var(--border)', margin:'0 4px' }}/>
                  {domains.map(d=>(
                    <button key={d.id} onClick={()=>setDomain(activeDomain===d.slug?'':d.slug)} style={{ padding:'5px 12px', borderRadius:20, fontSize:'0.75rem', cursor:'pointer', fontWeight:activeDomain===d.slug?600:400, background:activeDomain===d.slug?'rgba(232,164,58,0.12)':'var(--bg-2)', color:activeDomain===d.slug?'var(--gold)':'var(--t-secondary)', border:`1px solid ${activeDomain===d.slug?'rgba(232,164,58,0.35)':'var(--border)'}`, transition:'all 0.15s' }}>
                      {d.name}
                    </button>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <section style={{ padding:'clamp(40px,6vw,80px) 0', minHeight:'60vh' }}>
        <div className="container">
          {loading ? (
            <div style={{ textAlign:'center', padding:80, color:'var(--t-muted)' }}>Loading…</div>
          ) : tab === 'programs' ? (
            programs.length === 0 ? (
              <div style={{ textAlign:'center', padding:80 }}>
                <div style={{ fontSize:'2rem', opacity:0.3, marginBottom:12 }}>◐</div>
                <div style={{ color:'var(--t-muted)', marginBottom:16 }}>No programs found</div>
                <button onClick={()=>{setType('');setDomain('');}} className="btn btn-outline" style={{ margin:'0 auto' }}>Clear Filters</button>
              </div>
            ) : (
              <>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
                  <span style={{ fontSize:'0.82rem', color:'var(--t-muted)' }}>{programs.length} program{programs.length!==1?'s':''}</span>
                  <div style={{ display:'flex', gap:4 }}>
                    {[['grid','⊞'],['list','☰']].map(([v,icon])=>(
                      <button key={v} onClick={()=>setViewMode(v)} style={{ padding:'6px 12px', borderRadius:8, border:`1px solid ${viewMode===v?'var(--gold)':'var(--border)'}`, background:viewMode===v?'rgba(232,164,58,0.1)':'transparent', color:viewMode===v?'var(--gold)':'var(--t-muted)', cursor:'pointer', fontSize:'0.85rem', transition:'all 0.15s' }}>{icon}</button>
                    ))}
                  </div>
                </div>
                {viewMode==='grid' ? (
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(300px,1fr))', gap:20 }}>
                    {programs.map((p,i)=><ProgramCard key={p.id} program={p} idx={i}/>)}
                  </div>
                ) : (
                  <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                    {programs.map((p,i)=><ProgramListRow key={p.id} program={p} idx={i}/>)}
                  </div>
                )}
              </>
            )
          ) : (
            internships.length === 0 ? (
              <div style={{ textAlign:'center', padding:80 }}>
                <div style={{ fontSize:'2rem', opacity:0.3, marginBottom:12 }}>◉</div>
                <div style={{ color:'var(--t-muted)', marginBottom:16 }}>No open internships right now</div>
                <Link to="/contact?type=volunteer" className="btn btn-outline" style={{ margin:'0 auto' }}>Express Interest →</Link>
              </div>
            ) : (
              <>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
                  <span style={{ fontSize:'0.82rem', color:'var(--t-muted)' }}>{internships.length} open internship{internships.length!==1?'s':''}</span>
                  <div style={{ display:'flex', gap:4 }}>
                    {[['grid','⊞'],['list','☰']].map(([v,icon])=>(
                      <button key={v} onClick={()=>setViewMode(v)} style={{ padding:'6px 12px', borderRadius:8, border:`1px solid ${viewMode===v?'var(--gold)':'var(--border)'}`, background:viewMode===v?'rgba(232,164,58,0.1)':'transparent', color:viewMode===v?'var(--gold)':'var(--t-muted)', cursor:'pointer', fontSize:'0.85rem', transition:'all 0.15s' }}>{icon}</button>
                    ))}
                  </div>
                </div>
                {viewMode==='grid' ? (
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(300px,1fr))', gap:20 }}>
                    {internships.map((i,idx)=>(
                      <InternshipCard key={i.id} internship={i} idx={idx} basePath="/academy/internships"/>
                    ))}
                  </div>
                ) : (
                  <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                    {internships.map((i,idx)=><InternshipListRow key={i.id} internship={i}/>)}
                  </div>
                )}
              </>
            )
          )}
        </div>
      </section>

      {/* Bottom CTA */}
      <section style={{ background:'var(--bg-1)', borderTop:'1px solid var(--border)', padding:'clamp(40px,5vw,60px) 0' }}>
        <div className="container" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
          <div style={{ padding:'28px', background:'var(--bg-2)', borderRadius:16, border:'1px solid var(--border)' }}>
            <div style={{ fontFamily:'var(--font-display)', fontSize:'1.1rem', fontWeight:600, marginBottom:8 }}>Ready to learn?</div>
            <p style={{ color:'var(--t-secondary)', fontSize:'0.88rem', lineHeight:1.7, marginBottom:16 }}>Browse our programs and apply. Most cohorts start monthly.</p>
            <button onClick={()=>setTab('programs')} className="btn btn-primary" style={{ fontSize:'0.88rem' }}>Browse Programs →</button>
          </div>
          <div style={{ padding:'28px', background:'var(--bg-2)', borderRadius:16, border:'1px solid var(--border)' }}>
            <div style={{ fontFamily:'var(--font-display)', fontSize:'1.1rem', fontWeight:600, marginBottom:8 }}>Want hands-on experience?</div>
            <p style={{ color:'var(--t-secondary)', fontSize:'0.88rem', lineHeight:1.7, marginBottom:16 }}>Join the MilleHills team and work on real projects.</p>
            <button onClick={()=>setTab('internships')} className="btn btn-outline" style={{ fontSize:'0.88rem' }}>View Internships →</button>
          </div>
        </div>
      </section>
    </div>
  );
}